<script setup lang="ts">
import { useSiteWeeklyLimits } from "@/api/weekly-limit-query";
import { useDashboard } from "@/stores";
import {
	DataDefinition,
	DataType,
	DustDataType,
	VibrationSubType,
	WeatherDataType,
} from "@soft-db/data-formatter";
import { WeeklyLimitSummary } from "@soft-db/monitoring-api-client";
import { fromBase64 } from "@soft-db/soft-utils";
import { FormField, Select, SelectOption } from "@soft-db/ui";
import type { LiteralToPrimitiveDeep, UnionToIntersection } from "type-fest";

type IntersectedDefinition = UnionToIntersection<LiteralToPrimitiveDeep<DataDefinition>>;

const props = defineProps<{
	type?: DataType | "";
	subType?: VibrationSubType | WeatherDataType | DustDataType | "";
}>();

const exceedance = defineModel<string | null>("exceedance", { default: null });
const warning = defineModel<string | null>("warning", { default: null });

const { t } = useI18n();
const dashboardStore = useDashboard();


const { weeklyLimits } = useSiteWeeklyLimits(computed(() => dashboardStore.context?.site || ""));

const weeklyLimitOptions = computed<WeeklyLimitSummary[]>(() => {
	const options: WeeklyLimitSummary[] = [
		{ id: null as any, name: t("common.none"), query: { query: "", version: 0 } },
	];

	if (weeklyLimits.value) {
		for (const limit of weeklyLimits.value) {
			const limitDefinition: IntersectedDefinition = JSON.parse(fromBase64(limit.query.query));

			const subTypeIsMissing = !limitDefinition.subType && !props.subType;
			const subTypeIsTheSame = limitDefinition.subType === props.subType;
			const subTypeIsInData = !limitDefinition.subType && props.subType === limitDefinition.data;

			const isSubTypeCompatible = subTypeIsMissing || subTypeIsTheSame || subTypeIsInData;

			if (limitDefinition.type === props.type && isSubTypeCompatible) {
				options.push(limit);
			}
		}
	}

	return options;
});

function resetLimitIfNotFound(limit: Ref<string | null>) {
	if (weeklyLimits.value == null) {
		return;
	}

	if (limit.value == null || limit.value === "") {
		return;
	}

	const limitExist = weeklyLimits.value.some((l) => l.id === limit.value);
	if (!limitExist) {
		limit.value = null;
	}
}

watch([weeklyLimits, exceedance], () => resetLimitIfNotFound(exceedance));
watch([weeklyLimits, warning], () => resetLimitIfNotFound(warning));

watch([() => props.type, () => props.subType], () => {
	const hasExceedance = weeklyLimitOptions.value.some((l) => l.id === exceedance.value);
	const hasWarning = weeklyLimitOptions.value.some((l) => l.id === warning.value);

	if (!hasExceedance) {
		exceedance.value = "";
	}

	if (!hasWarning) {
		warning.value = "";
	}
});
</script>

<template>
	<div>
		<h3>{{ t("common.weeklyLimit") }}</h3>
		<div class="auto-grid gap-4">
			<FormField :label="t('dashboard.weeklyLimitExceedance')">
				<Select v-model="exceedance" class="w-full">
					<SelectOption v-for="option of weeklyLimitOptions" :key="option.id" :value="option.id">
						{{ option.name }}
					</SelectOption>
				</Select>
			</FormField>

			<FormField :label="t('dashboard.weeklyLimitWarning')">
				<Select v-model="warning" class="w-full">
					<SelectOption v-for="option of weeklyLimitOptions" :key="option.id" :value="option.id">
						{{ option.name }}
					</SelectOption>
				</Select>
			</FormField>
		</div>
	</div>
</template>
